import type {
  BlueprintConnection,
  BlueprintDocument,
  ConnectionType,
  DeploymentSettings,
  Placement,
  ResourceType,
  WiringConnectionType,
  WiringRule,
} from '../api/types';

export const GRID = 20;

export function emptyBlueprint(name: string): BlueprintDocument {
  return { schemaVersion: 1, name, resources: [], connections: [] };
}

/** `<last part of type>-<n>` with the lowest free n, for example `webapp-1`. */
export function nextResourceId(doc: BlueprintDocument, type: string): string {
  const base =
    (type.split('.').pop() ?? 'resource')
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/^[^a-z]+/, '')
      .slice(0, 25) || 'resource';
  const used = new Set(doc.resources.map((r) => r.id));
  let n = 1;
  while (used.has(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}

/** The alias used when a `connects-to` alias is omitted: target id uppercased, `-` replaced by `_`. */
export function defaultAlias(targetId: string): string {
  return targetId.toUpperCase().replace(/-/g, '_');
}

/** The alias used when a `triggers` alias is omitted: the source id in PascalCase (`orders-queue` → `OrdersQueue`). */
export function defaultTriggerAlias(sourceId: string): string {
  return sourceId
    .split('-')
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join('');
}

/** The effective alias of a connection (explicit or derived); undefined for `depends-on`. */
export function effectiveAlias(connection: BlueprintConnection): string | undefined {
  if (connection.type === 'connects-to') return connection.alias || defaultAlias(connection.targetId);
  if (connection.type === 'triggers') return connection.alias || defaultTriggerAlias(connection.sourceId);
  return undefined;
}

export const CONNECTS_TO_ALIAS_PATTERN = /^[A-Z][A-Z0-9_]{0,31}$/;
export const TRIGGER_ALIAS_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
export const FUNCTION_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;

/** The wiring rule of a pair for one connection type (rules without `connectionType` are `connects-to`). */
export function wiringRule(
  types: ReadonlyMap<string, ResourceType>,
  sourceType: string | undefined,
  targetType: string | undefined,
  connectionType: WiringConnectionType = 'connects-to',
): WiringRule | undefined {
  if (!sourceType || !targetType) return undefined;
  return types
    .get(sourceType)
    ?.connections.find(
      (rule) => rule.targetType === targetType && (rule.connectionType ?? 'connects-to') === connectionType,
    );
}

/** Connection types offered between two resources: connects-to and triggers only with a wiring rule. */
export function allowedConnectionTypes(
  types: ReadonlyMap<string, ResourceType>,
  sourceType: string | undefined,
  targetType: string | undefined,
): ConnectionType[] {
  const result: ConnectionType[] = [];
  if (wiringRule(types, sourceType, targetType, 'connects-to')) result.push('connects-to');
  if (wiringRule(types, sourceType, targetType, 'triggers')) result.push('triggers');
  result.push('depends-on');
  return result;
}

/** Whether a type is the target of any `triggers` rule (for example `compute.function`). */
export function isTriggerTarget(types: ReadonlyMap<string, ResourceType>, type: string | undefined): boolean {
  if (!type) return false;
  for (const candidate of types.values()) {
    if (candidate.connections.some((r) => r.connectionType === 'triggers' && r.targetType === type))
      return true;
  }
  return false;
}

export interface EnvironmentVariable {
  name: string;
  secret: boolean;
  fromOutput: string;
}

/** Environment variables the source receives through a connects-to connection: `<ALIAS>_<KEY>`. */
export function connectionEnvironment(
  connection: BlueprintConnection,
  rule: WiringRule | undefined,
): EnvironmentVariable[] {
  if (connection.type !== 'connects-to' || !rule || (rule.connectionType ?? 'connects-to') !== 'connects-to')
    return [];
  const alias = connection.alias || defaultAlias(connection.targetId);
  return rule.keys.map((key) => ({
    name: `${alias}_${key.key}`,
    secret: key.secret,
    fromOutput: key.fromOutput,
  }));
}

export function hasConnection(doc: BlueprintDocument, connection: BlueprintConnection): boolean {
  return (doc.connections ?? []).some(
    (c) =>
      c.sourceId === connection.sourceId && c.targetId === connection.targetId && c.type === connection.type,
  );
}

/**
 * Providers a deployment uses: default placement, blueprint placements and overrides (spec 6.4 step 5),
 * plus the vault's provider when the blueprint declares secrets.
 */
export function providersUsed(
  doc: BlueprintDocument,
  settings: Pick<DeploymentSettings, 'defaultPlacement' | 'placementOverrides' | 'vault'>,
): string[] {
  const providers = new Set<string>();
  for (const resource of doc.resources) {
    const provider =
      settings.placementOverrides[resource.id]?.provider ||
      resource.placement?.provider ||
      settings.defaultPlacement?.provider;
    if (provider) providers.add(provider);
  }
  if (doc.resources.length === 0 && settings.defaultPlacement?.provider) {
    providers.add(settings.defaultPlacement.provider);
  }
  if (settings.vault?.provider && Object.keys(doc.secrets ?? {}).length > 0) {
    providers.add(settings.vault.provider);
  }
  return [...providers].sort();
}

/** The vault's placement: `vault` field by field over `defaultPlacement` (spec 12, 3.2). */
export function vaultPlacement(settings: Pick<DeploymentSettings, 'defaultPlacement' | 'vault'>): Placement {
  return {
    provider: settings.vault?.provider || settings.defaultPlacement?.provider || undefined,
    region: settings.vault?.region || settings.defaultPlacement?.region || undefined,
  };
}

export function cleanPlacement(placement: Placement | undefined): Placement | undefined {
  if (!placement) return undefined;
  const result: Placement = {};
  if (placement.provider) result.provider = placement.provider;
  if (placement.region) result.region = placement.region;
  return Object.keys(result).length ? result : undefined;
}

export function snapToGrid(value: number): number {
  return Math.round(value / GRID) * GRID;
}

/** The node position for a resource without a stored layout entry. */
export function fallbackPosition(index: number): { x: number; y: number } {
  return { x: 40 + (index % 4) * 240, y: 40 + Math.floor(index / 4) * 160 };
}
